import { useCallback, useState } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  ClipboardList,
  Check,
  X,
  Loader2,
  Plus,
  RotateCcw,
  Sparkles,
  History,
} from 'lucide-react';
import type { useJobAssessment } from '@/hooks/useJobAssessment';
import type { SavedJob } from '@/lib/types';
import type { Dimension } from '@/lib/assessment';
import { DIMENSION_META, averageScore, quizFromReview } from '@/lib/assessment';
import { Button } from '@/components/ui/button';
import { QuizSession } from './QuizSession';
import { AssessmentResults } from './AssessmentResults';
import { BackToListButton } from '@/components/BackToListButton';

type Props = {
  job: SavedJob;
  /**
   * The assessment state machine, owned by the parent (JobDetail) so it
   * survives detail↔panel navigation — e.g. a generation kicked off in the
   * panel keeps running if the user pops back to the detail view.
   */
  assessment: ReturnType<typeof useJobAssessment>;
  onBack: () => void;
  /** Jump straight to the Dashboard jobs list (unmounts this whole subtree). */
  onHome: () => void;
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function levelTone(score: number): string {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-sky-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-rose-600';
}

export function AssessmentPanel({ job, assessment, onBack, onHome }: Props) {
  const {
    phase,
    quiz,
    takingQuiz,
    history,
    result,
    selectedReview,
    generationsRemaining,
    pendingDimension,
    quizGenerating,
    error,
    generateQuiz,
    startTaking,
    submit,
    backToOverview,
    reviewAttempt,
    dismissError,
  } = assessment;

  // Quota is counted in half-generation units server-side, so the remaining
  // number can be fractional (e.g. 9.5). A full quiz costs 1.0; a single-
  // dimension practice costs 0.5.
  const canFullGen = generationsRemaining === null || generationsRemaining >= 1;
  const canPractice = generationsRemaining === null || generationsRemaining >= 0.5;
  // Derived from the generating FLAG (not phase) so the "generating" state
  // stays accurate even if the user navigates away from the loading screen
  // mid-generation — the poll keeps running in the background.
  const quizLoading = quizGenerating;

  // Two-step confirm for generation AND take actions. Only one confirm flow is
  // active at a time; the cluster surfaces the cost (generation) or the scope
  // (take: which assessment will be attempted) before proceeding.
  const [confirming, setConfirming] = useState<
    | { kind: 'practice'; dim: Dimension }
    | { kind: 'full' }
    | { kind: 'take' }
    | null
  >(null);

  // The single-dimension "generate" buttons mirror the primary generate
  // button's icon so all generate-type actions read as one family: Sparkles
  // for a first-time generate, RotateCcw once a quiz already exists.
  const GenIcon = quiz ? RotateCcw : Sparkles;

  // Retake a reviewed past attempt against ITS OWN questions. Without this, a
  // Retake from the Past Attempts list would fall through to the latest
  // generated quiz (state.quiz), re-asking a different question set than the
  // one the user is looking at. We rebuild a takeable quiz from the attempt's
  // review and submit against its original quizId.
  const retakeReview = useCallback(() => {
    if (!selectedReview) return;
    startTaking(
      quizFromReview(selectedReview, {
        jobId: job.id,
        jobTitle: job.title,
        language: quiz?.language ?? 'en',
      }),
    );
  }, [selectedReview, job, quiz, startTaking]);

  // Full-screen taking view.
  if (phase === 'taking' && takingQuiz) {
    return (
      <QuizSession
        quiz={takingQuiz}
        submitting={false}
        onSubmit={(answers) => void submit(answers)}
        onCancel={backToOverview}
        onHome={onHome}
      />
    );
  }

  // Full-screen submitting view (kept compact; the session shows a spinner).
  if (phase === 'submitting' && takingQuiz) {
    return (
      <QuizSession
        quiz={takingQuiz}
        submitting
        onSubmit={(answers) => void submit(answers)}
        onCancel={backToOverview}
        onHome={onHome}
      />
    );
  }

  // Results view (just-submitted attempt).
  if (phase === 'results' && result) {
    return (
      <AssessmentResults
        result={result}
        onRetake={startTaking}
        onBack={backToOverview}
        onHome={onHome}
      />
    );
  }

  // Review-past-attempt view. Reuses AssessmentResults verbatim so the
  // reveal/hide-answers UX is identical to the post-submit view. `key` forces
  // a fresh instance per attempt so the local showAnswers state resets.
  if (phase === 'reviewing') {
    if (!selectedReview) {
      // Fetch in flight — show a compact spinner that mirrors the panel's
      // other loading affordances.
      return (
        <div className="flex min-h-dvh flex-col bg-background">
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-card/95 px-2 py-2 backdrop-blur">
            <Button variant="ghost" size="sm" onClick={backToOverview} className="gap-1">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <span className="text-xs font-medium text-muted-foreground">Loading…</span>
          </div>
          <div className="flex flex-1 items-center justify-center p-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        </div>
      );
    }
    return (
      <AssessmentResults
        key={selectedReview.id}
        result={selectedReview}
        onRetake={retakeReview}
        onBack={backToOverview}
        onHome={onHome}
      />
    );
  }

  const totalQuestions = quiz ? quiz.willingness.length + quiz.hard.length + quiz.soft.length : 0;

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-card/95 px-2 py-2 backdrop-blur">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <span className="text-xs font-medium text-muted-foreground">Self-assessment</span>
      </div>

      <div className="mx-auto w-full max-w-2xl px-4 pt-4 pb-24 sm:px-6">
        <div className="flex items-start gap-2.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ClipboardList className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold leading-snug tracking-tight">
              {job.title}
            </h1>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {quiz
                ? 'Assessment ready — retake anytime to track progress.'
                : 'Generate a full or specific dimension self-assessment from the JD breakdown.'}
            </p>
          </div>
        </div>

        {error ? (
          <div className="mt-4 flex items-start justify-between gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <span className="min-w-0 break-words">{error}</span>
            <button
              type="button"
              onClick={dismissError}
              className="shrink-0 text-destructive/70 hover:text-destructive"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        ) : null}

        {/* In-flight banner — labels a single-dimension assessment so the user
            knows which generate click is running (the buttons themselves all
            disable during loading). */}
        {quizLoading ? (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-primary">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {pendingDimension
              ? `Generating ${DIMENSION_META[pendingDimension].label} assessment — this takes ~40s; feel free to come back, it keeps running.`
              : 'Generating your self-assessment quiz — this takes ~2min. Feel free to come back; it keeps running.'}
          </div>
        ) : null}

        {/* Funnel explainer — the three assessment dimensions. Each card has a
            generate action (mirrors the primary generate button's icon) that
            produces a single-dimension assessment (0.5 gen) so the user can
            drill on one dimension they want to improve. */}
        <div className="mt-4 grid grid-cols-1 gap-2">
          {(['willingness', 'hard_requirements', 'soft_requirements'] as const).map((dim, i) => {
            const meta = DIMENSION_META[dim];
            const Icon = meta.icon;
            const desc =
              dim === 'willingness'
                ? 'Should I actually apply? (interest & fit)'
                : dim === 'hard_requirements'
                  ? 'Will I pass resume screening? (ATS gates)'
                  : 'Will I pass the interview? (soft capabilities)';
            return (
              <div
                key={dim}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-3.5 py-2.5"
              >
                <Icon className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">
                    <span className="mr-1 text-muted-foreground">{i + 1}.</span>
                    {meta.label}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{desc}</p>
                </div>
                {confirming?.kind === 'practice' && confirming.dim === dim ? (
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className="text-[11px] font-medium text-amber-600">0.5 gen</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Cancel"
                      aria-label={`Cancel ${meta.label} assessment`}
                      onClick={() => setConfirming(null)}
                      className="h-7 w-7 bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Confirm"
                      aria-label={`Confirm ${meta.label} assessment`}
                      autoFocus
                      onClick={() => {
                        setConfirming(null);
                        void generateQuiz(dim);
                      }}
                      className="h-7 w-7 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-600"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirming({ kind: 'practice', dim })}
                    disabled={quizLoading || !canPractice}
                    className="shrink-0 gap-1.5"
                    title={
                      canPractice
                        ? `Generate a ${meta.label} assessment (costs 0.5 generation)`
                        : 'Not enough generations left'
                    }
                  >
                    <GenIcon className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Assessment actions */}
        <div className="mt-4 flex flex-col gap-2">
          {!quiz ? (
            quizLoading ? (
              <Button disabled className="gap-1.5">
                <Loader2 className="h-4 w-4 animate-spin" /> Generating assessment…
              </Button>
            ) : confirming?.kind === 'full' ? (
              <div className="flex items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600">
                  <Sparkles className="h-3.5 w-3.5" /> Uses 1 generation
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Cancel"
                  aria-label="Cancel"
                  onClick={() => setConfirming(null)}
                  className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Confirm"
                  aria-label="Confirm generate assessment"
                  autoFocus
                  onClick={() => {
                    setConfirming(null);
                    void generateQuiz();
                  }}
                  className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-600"
                >
                  <Check className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setConfirming({ kind: 'full' })}
                disabled={!canFullGen}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" /> Generate assessment
              </Button>
            )
          ) : (
            <>
              {/* Start assessment — text is always "Start assessment" regardless
                  of first-time vs retake; the two-step confirm reveals WHICH
                  assessment (full / single-dimension) so the distinction the
                  old "Take again" label carried is surfaced inline instead.
                  Disabled while a generation is in flight: the poll itself is
                  robust to navigation now, but taking the OLD quiz while a NEW
                  one is being generated is confusing (the take's questions
                  would be silently swapped out from under the user once the
                  new quiz lands). */}
              {confirming?.kind === 'take' ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary">
                    <Plus className="h-3.5 w-3.5" />
                    {quiz && quiz.dimensions.length === 1
                      ? `${DIMENSION_META[quiz.dimensions[0]].short} assessment`
                      : 'Full assessment'}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Cancel"
                    aria-label="Cancel start assessment"
                    onClick={() => setConfirming(null)}
                    className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Confirm"
                    aria-label="Confirm start assessment"
                    autoFocus
                    disabled={quizLoading}
                    onClick={() => {
                      setConfirming(null);
                      startTaking();
                    }}
                    className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-600"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setConfirming({ kind: 'take' })}
                  disabled={quizLoading}
                  className="gap-1.5"
                >
                  <Plus className="h-4 w-4" /> Start assessment
                </Button>
              )}
              {quizLoading ? (
                <Button variant="outline" disabled className="gap-1.5">
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating new assessment…
                </Button>
              ) : confirming?.kind === 'full' ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600">
                    <RotateCcw className="h-3.5 w-3.5" /> Uses 1 generation
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Cancel"
                    aria-label="Cancel"
                    onClick={() => setConfirming(null)}
                    className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Confirm"
                    aria-label="Confirm generate new assessment"
                    autoFocus
                    onClick={() => {
                      setConfirming(null);
                      void generateQuiz();
                    }}
                    className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-600"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setConfirming({ kind: 'full' })}
                  disabled={!canFullGen}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-4 w-4" /> Generate new assessment
                </Button>
              )}
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {totalQuestions} question{totalQuestions === 1 ? '' : 's'}
                {quiz.dimensions.length === 1
                  ? ` · ${DIMENSION_META[quiz.dimensions[0]].short} assessment`
                  : ' · Full assessment'}
                {' · '}
                {quiz.language.toUpperCase()}
              </p>
            </>
          )}

          {/* Quota counter */}
          {generationsRemaining !== null && (
            <p className="text-center text-xs text-muted-foreground">
              {generationsRemaining <= 0 ? (
                <>You've used all 10 generations.</>
              ) : (
                <>
                  {generationsRemaining} of 10 generation{generationsRemaining === 1 ? '' : 's'} remaining
                </>
              )}
            </p>
          )}
        </div>

        {/* Retake history */}
        {history.length > 0 ? (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <History className="h-3.5 w-3.5" /> Past attempts
            </div>
            <ul className="space-y-2">
              {history.map((h) => {
                const dims = (h.dimensions ?? ([
                  'willingness',
                  'hard_requirements',
                  'soft_requirements',
                ] as Dimension[])) as Array<keyof typeof DIMENSION_META>;
                const avg = averageScore(h);
                return (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => void reviewAttempt(h.id)}
                      // Full-width row, button-reset so layout matches a plain
                      // card. `focus-visible:ring` keeps keyboard a11y parity
                      // with the option buttons in QuizSession.
                      className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-card px-3.5 py-2.5 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Review attempt from ${timeAgo(h.createdAt)}`}
                    >
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {timeAgo(h.createdAt)}
                          <span className="rounded bg-secondary px-1 py-px text-[10px] font-medium uppercase tracking-wide text-secondary-foreground">
                            {dims.length === 1
                              ? `${DIMENSION_META[dims[0]].short} assessment`
                              : 'Full assessment'}
                          </span>
                        </p>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                          {dims.map((dim) => {
                            const score =
                              dim === 'willingness'
                                ? h.willingnessScore.score
                                : dim === 'hard_requirements'
                                  ? h.hardScore.score
                                  : h.softScore.score;
                            const Icon = DIMENSION_META[dim].icon;
                            return (
                              <span key={dim} className="inline-flex items-center gap-1">
                                <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
                                <span className={levelTone(score)}>{score}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            avg
                          </p>
                          <p className={levelTone(avg) + ' text-lg font-bold tabular-nums'}>
                            {avg}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Tap an attempt to review the questions and your answers.
            </p>
          </div>
        ) : null}

        <BackToListButton onHome={onHome} />
      </div>
    </div>
  );
}
